import React, { useEffect, useState, useMemo } from 'react';
import { useALCContext } from '../context/ALCContext';
import { alcService } from '../services/alcService';
import ALCStepper from './ALCStepper';
import moment from 'moment';
import { useMsal } from "@azure/msal-react";
import { SP_MSAL_SCOPE } from '../../../../constants/sharepoint';
import { ALC_CHECKLIST_SEED_DATA } from '../constants/alcSeedData';

const ALCSummary: React.FC = () => {
    const { state } = useALCContext();
    const [isLoading, setIsLoading] = useState(true);
    const [sessionData, setSessionData] = useState<any>(null);
    const [checkpoints, setCheckpoints] = useState<any[]>([]);
    const [configs, setConfigs] = useState<any[]>([]);
    const [dailyCount, setDailyCount] = useState(1);
    const [repeatabilityData, setRepeatabilityData] = useState<any[]>([]);

    const { instance, accounts } = useMsal();

    useEffect(() => {
        const loadData = async () => {
            if (!state.currentTourId) return;

            setIsLoading(true);
            try {
                // 1. Fetch Session
                const fetchedSession = await alcService.getSessionById(state.currentTourId);
                setSessionData(fetchedSession);

                // 2. Fetch Checkpoints
                const fetchedCheckpoints = await alcService.getCheckpoints(state.currentTourId);
                setCheckpoints(fetchedCheckpoints);

                // 3. Fetch Configs for Area Incharges & Question Criticality
                let spToken = "";
                try {
                    const tokenResponse = await instance.acquireTokenSilent({ scopes: [SP_MSAL_SCOPE], account: accounts[0] });
                    spToken = tokenResponse.accessToken;
                } catch (e) {
                    console.warn("Failed silent token for SP, falling back without token for config fetch.");
                }
                const fetchedConfigs = await alcService.getConfig(spToken);
                setConfigs(fetchedConfigs || []);

                // 4. Fetch daily tours count and repeatability data
                if (fetchedSession?.cr3ea_lineno) {
                    const recentSessions = await alcService.getActiveSessionsByLine(fetchedSession.cr3ea_lineno);
                    
                    const todayStr = moment().format("YYYY-MM-DD");
                    const count = (recentSessions || []).filter((s: any) => {
                        const sDate = s.createdon || s.cr3ea_tourstartdate;
                        if (!sDate) return false;
                        return moment(sDate).format("YYYY-MM-DD") === todayStr;
                    }).length;
                    setDailyCount(count > 0 ? count : 1);

                    // Repeatability: Check last 5 sessions on this line (excluding current)
                    const pastTours = (recentSessions || [])
                        .filter((s: any) => s.cr3ea_prod_qualitytourid !== state.currentTourId && s.cr3ea_prod_rajpura_quality_tourid !== state.currentTourId)
                        .slice(0, 5);

                    if (pastTours.length > 0) {
                        const currentFailed = fetchedCheckpoints.filter((cp: any) => {
                            const scoreText = cp.cr3ea_defectcategory || "";
                            return cp.cr3ea_status === "Not Okay" || scoreText.includes("Non-Compliant") || scoreText.includes("Partial") || scoreText === "00" || scoreText === "01";
                        });

                        if (currentFailed.length > 0) {
                            const pastCheckpointsPromises = pastTours.map((t: any) => 
                                alcService.getCheckpoints(t.cr3ea_prod_qualitytourid || t.cr3ea_prod_rajpura_quality_tourid)
                            );
                            const pastCheckpointsLists = await Promise.all(pastCheckpointsPromises);

                            const recurring: any[] = [];
                            currentFailed.forEach((curCp: any) => {
                                let failCount = 0;
                                pastCheckpointsLists.forEach((pastList: any[]) => {
                                    const match = (pastList || []).find(pc => pc.cr3ea_criteria === curCp.cr3ea_criteria);
                                    if (match) {
                                        const statusText = match.cr3ea_defectcategory || "";
                                        const failed = match.cr3ea_status === "Not Okay" || statusText.includes("Non-Compliant") || statusText.includes("Partial") || statusText === "00" || statusText === "01";
                                        if (failed) failCount++;
                                    }
                                });
                                if (failCount > 0) {
                                    recurring.push({ area: curCp.cr3ea_area, criteria: curCp.cr3ea_criteria, times: failCount, totalTours: pastTours.length });
                                }
                            });
                            setRepeatabilityData(recurring);
                        }
                    }
                }

            } catch (error) {
                console.error("Error loading ALC summary data", error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [state.currentTourId, instance, accounts]);

    // Parse Escalation & Delay Audit metadata
    const auditData = useMemo(() => {
        if (!sessionData) return null;
        const escContacts = sessionData.cr3ea_escalation_contacts || "";
        if (!escContacts.includes("AUDIT:")) return null;

        try {
            const auditJsonStr = escContacts.split("AUDIT:")[1].trim();
            return JSON.parse(auditJsonStr);
        } catch (e) {
            console.warn("Failed to parse escalation audit metadata:", e);
            return null;
        }
    }, [sessionData]);

    const isTourDelayed = useMemo(() => {
        if (!sessionData) return false;
        const status = (sessionData.cr3ea_processstatus || sessionData.cr3ea_status || "").trim();
        return (auditData && auditData.isPostEscalation) || status.includes("Delayed") || status.includes("Escalated");
    }, [sessionData, auditData]);

    // Resolve Area Incharges mapping
    const areaInchargeCards = useMemo(() => {
        if (!checkpoints.length || !configs.length) return [];
        const tourAreas = [...new Set(checkpoints.map(cp => cp.cr3ea_area).filter(Boolean))];
        const mappings: { area: string; names: string }[] = [];

        tourAreas.forEach(areaName => {
            const configRow = configs.find(c =>
                (c.ConfigType === "Product User" || c.ConfigType === "Area Inspector" || c.ConfigType === "Product Incharge") &&
                c.Area &&
                (c.Area.toLowerCase().includes(areaName.toLowerCase().trim()) || areaName.toLowerCase().trim().includes(c.Area.toLowerCase()))
            );
            if (configRow?.AssignedUser?.results) {
                const names = configRow.AssignedUser.results.map((u: any) => u.Title).join(", ");
                if (names) mappings.push({ area: areaName, names });
            }
        });
        return mappings;
    }, [checkpoints, configs]);

    // Question configs for criticality
    const questionConfigs = useMemo(() => {
        return (configs || []).filter(c => c.ConfigType === "Checklist Question");
    }, [configs]);

    // Score calculations, Critical Gate evaluation, Area-wise stats
    const stats = useMemo(() => {
        let totalMaxPoints = checkpoints.length * 2;
        let totalObtainedPoints = 0;
        let hasCriticalFailure = false;
        const failedCriticalQuestions: any[] = [];
        const areaStats: Record<string, { total: number, obtained: number }> = {};

        checkpoints.forEach(cp => {
            const area = cp.cr3ea_area || "General";
            if (!areaStats[area]) areaStats[area] = { total: 0, obtained: 0 };
            
            areaStats[area].total += 2;

            let numericScore = 2;
            const scoreText = cp.cr3ea_defectcategory || "";
            if (scoreText.includes("(0)") || scoreText === "00" || scoreText.includes("Non-Compliant")) {
                numericScore = 0;
            } else if (scoreText.includes("(1)") || scoreText === "01" || scoreText.includes("Partial")) {
                numericScore = 1;
            }

            // Critical gate lookup
            let isCritical = false;
            const criteriaText = (cp.cr3ea_criteria || "").toLowerCase().trim();
            const matchedCfg = questionConfigs.find(q => {
                const qTitle = (q.Title || "").toLowerCase().trim();
                const qRemarks = (q.Remarks || "").toLowerCase().trim();
                return (qTitle && (criteriaText.includes(qTitle) || qTitle.includes(criteriaText))) ||
                       (qRemarks && (criteriaText.includes(qRemarks) || qRemarks.includes(criteriaText)));
            });

            if (matchedCfg) {
                isCritical = !!(matchedCfg.isCritical ||
                    matchedCfg.IsCritical === true ||
                    matchedCfg.ProductCategory === "Critical" ||
                    matchedCfg.Remarks === "Critical");
            } else {
                const seedItem = ALC_CHECKLIST_SEED_DATA.find(s => {
                    const sText = (s.title || "").toLowerCase().trim();
                    return sText && (criteriaText.includes(sText) || sText.includes(criteriaText));
                });
                if (seedItem) {
                    isCritical = !!seedItem.isCritical;
                }
            }

            cp._isCritical = isCritical;

            if (isCritical && (numericScore === 0 || numericScore === 1)) {
                hasCriticalFailure = true;
                failedCriticalQuestions.push({
                    criteria: cp.cr3ea_criteria || "Critical Item",
                    area: area,
                    scoreText: scoreText || (numericScore === 0 ? "Non-Compliant (0)" : "Partial (1)")
                });
            }

            totalObtainedPoints += numericScore;
            areaStats[area].obtained += numericScore;
        });

        const overallPercentRaw = totalMaxPoints > 0 ? (totalObtainedPoints / totalMaxPoints) * 100 : 0;
        return {
            totalMaxPoints,
            totalObtainedPoints,
            overallPercent: overallPercentRaw.toFixed(2),
            hasCriticalFailure,
            failedCriticalQuestions,
            areaStats
        };
    }, [checkpoints, questionConfigs]);

    // Group checkpoints by area
    const checkpointsByArea = useMemo(() => {
        const groups: Record<string, any[]> = {};
        checkpoints.forEach(cp => {
            const area = cp.cr3ea_area || "General";
            if (!groups[area]) groups[area] = [];
            groups[area].push(cp);
        });
        return groups;
    }, [checkpoints]);

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div>
                <p className="mt-3 text-muted">Generating Line Clearance Summary Report...</p>
            </div>
        );
    }

    if (!sessionData) {
        return <div className="alert alert-danger">Could not load session summary.</div>;
    }

    const dateStr = sessionData.cr3ea_tourstartdate ? moment(sessionData.cr3ea_tourstartdate).format("DD-MM-YYYY hh:mm A") : "N/A";
    const overallScorePercent = parseFloat(stats.overallPercent);
    const isPassingScore = (overallScorePercent >= 80) && !stats.hasCriticalFailure;
    
    const status = sessionData.cr3ea_processstatus || sessionData.cr3ea_status || "";
    const isExpired = status === "Closed - Expired" || status.includes("Expired");
    const hasNoScore = (stats.totalMaxPoints === 0 || stats.totalObtainedPoints === 0);

    const scoreColor = (isExpired && hasNoScore) ? "#ef4444" : (isPassingScore ? "#10b981" : "#ef4444");
    const scoreShadow = (isExpired && hasNoScore) ? "rgba(239, 68, 68, 0.3)" : (isPassingScore ? "rgba(16, 185, 129, 0.3)" : "rgba(239, 68, 68, 0.3)");

    let scoreDescNode: React.ReactNode = <span>Completed with {stats.overallPercent}% compliance score.</span>;
    if (isExpired && hasNoScore) {
        scoreDescNode = <span style={{ color: '#b91c1c' }}>Expired while in QA process / In Progress</span>;
    } else if (stats.hasCriticalFailure) {
        const critCount = stats.failedCriticalQuestions.length;
        scoreDescNode = (
            <span style={{ color: '#b91c1c' }}>
                <strong style={{ color: '#b91c1c' }}>Failed - Critical Gate Violation:</strong> {critCount} Critical checkpoint{critCount > 1 ? 's' : ''} failed (Score: {stats.overallPercent}%). Tour cannot pass clearance.
            </span>
        );
    } else if (status === "Closed - Expired") {
        scoreDescNode = (
            <span style={{ color: isPassingScore ? '#047857' : '#b91c1c' }}>
                {isPassingScore ? "Success" : "Failed"} - Expired with {stats.overallPercent}% compliance score.
            </span>
        );
    } else if (status.includes("Pending Production")) {
        scoreDescNode = (
            <span style={{ color: isPassingScore ? '#047857' : '#b91c1c' }}>
                Pending Production Corrective Actions. Current Score: {stats.overallPercent}%
            </span>
        );
    } else if (status.includes("Pending Re-Verification")) {
        scoreDescNode = (
            <span style={{ color: isPassingScore ? '#047857' : '#b91c1c' }}>
                Pending QA Re-Verification. Current Score: {stats.overallPercent}%
            </span>
        );
    } else if (status === "Completed" || status === "Success") {
        scoreDescNode = <span style={{ color: '#047857' }}>Excellent! 100% compliance cleared successfully.</span>;
    }

    return (
        <div>
            <ALCStepper />

            <div className="bs-card" style={{ padding: '25px', border: '1px solid #10b981', borderRadius: '8px', backgroundColor: '#ffffff', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                
                {/* Header & Print Button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #f1f5f9', paddingBottom: '15px', marginBottom: '20px' }}>
                    <h3 style={{ fontWeight: 'bold', color: '#0f766e', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Line Clearance Summary Report
                    </h3>
                    <div className="no-print">
                        <button 
                            type="button" 
                            className="bs-btn bs-btn-secondary print-btn" 
                            onClick={() => window.print()} 
                            style={{ backgroundColor: '#f1f5f9', color: '#1e293b', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                        >
                            🖨️ Print Report
                        </button>
                    </div>
                </div>

                {/* 1. Basic Metadata Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 15px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Shift Executive (Prod)</span>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginTop: '4px' }}>
                            {sessionData.cr3ea_shiftexecutiveproduction || "N/A"}
                        </div>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 15px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>QA Shift Executive</span>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginTop: '4px' }}>
                            {sessionData.cr3ea_executivename || sessionData.cr3ea_shiftexecutivequality || "—"}
                        </div>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 15px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>QA Executive</span>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginTop: '4px' }}>
                            {sessionData.cr3ea_tourby || sessionData.cr3ea_observedby || "N/A"}
                        </div>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 15px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Conducted Date & Time</span>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginTop: '4px' }}>
                            {dateStr}
                        </div>
                    </div>
                    <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 15px', borderRadius: '6px' }}>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Line / Shift</span>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginTop: '4px' }}>
                            {sessionData.cr3ea_lineno || "N/A"} / {sessionData.cr3ea_shift || "N/A"}
                        </div>
                    </div>
                </div>

                {/* 2. Changeover Variety Details Card */}
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '25px', overflow: 'hidden' }}>
                    <div style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 15px', fontWeight: 'bold', fontSize: '14px', color: '#1e293b' }}>
                        Changeover Variety Details
                    </div>
                    <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-around', alignItems: 'center', flexWrap: 'wrap', gap: '15px', textAlign: 'center' }}>
                        <div>
                            <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Previous Variety</span>
                            <h4 style={{ margin: '5px 0 0 0', fontWeight: 'bold', color: '#475569', fontSize: '16px' }}>
                                {sessionData.cr3ea_previousrunningvariety || "--"}
                            </h4>
                        </div>
                        <div style={{ fontSize: '24px', color: '#94a3b8' }}>➔</div>
                        <div>
                            <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>Running Variety</span>
                            <h4 style={{ margin: '5px 0 0 0', fontWeight: 'bold', color: '#0f766e', fontSize: '16px' }}>
                                {sessionData.cr3ea_runningvariety || "--"}
                            </h4>
                        </div>
                    </div>
                </div>

                {/* 3. Acceptance & Escalation Timing Audit Card (when delayed/escalated) */}
                {isTourDelayed && (
                    <div id="sum-escalation-audit-panel" style={{ padding: '18px 20px', borderRadius: '8px', border: '1px solid #fed7aa', backgroundColor: '#fffaf5', marginBottom: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px', borderBottom: '1px solid #ffedd5', paddingBottom: '10px' }}>
                            <h5 style={{ margin: 0, fontWeight: 700, color: '#9a3412', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ⏱️ Acceptance &amp; Escalation Timing Audit
                            </h5>
                            <span style={{ backgroundColor: '#ffedd5', color: '#c2410c', border: '1px solid #fed7aa', fontWeight: 700, padding: '4px 10px', fontSize: '12px', borderRadius: '9999px' }}>
                                {auditData?.delayMinutes ? `${auditData.delayMinutes} Min Acceptance Delay` : 'Post-Escalation Accepted'}
                            </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', fontSize: '13px' }}>
                            <div style={{ backgroundColor: '#ffffff', border: '1px solid #fed7aa', padding: '10px 14px', borderRadius: '6px' }}>
                                <span style={{ fontSize: '10px', textTransform: 'uppercase', color: '#7c2d12', fontWeight: 700, display: 'block' }}>Request Raised At</span>
                                <strong style={{ color: '#1e293b', fontSize: '13px' }}>
                                    {auditData?.tourStartTime ? moment(auditData.tourStartTime).format("DD-MM-YYYY hh:mm A") : (sessionData.cr3ea_tourstartdate ? moment(sessionData.cr3ea_tourstartdate).format("DD-MM-YYYY hh:mm A") : "N/A")}
                                </strong>
                            </div>
                            <div style={{ backgroundColor: '#ffffff', border: '1px solid #fed7aa', padding: '10px 14px', borderRadius: '6px' }}>
                                <span style={{ fontSize: '10px', textTransform: 'uppercase', color: '#7c2d12', fontWeight: 700, display: 'block' }}>Escalation Threshold (5m)</span>
                                <strong style={{ color: '#dc2626', fontSize: '13px' }}>Expired (Escalated)</strong>
                            </div>
                            <div style={{ backgroundColor: '#ffffff', border: '1px solid #fed7aa', padding: '10px 14px', borderRadius: '6px' }}>
                                <span style={{ fontSize: '10px', textTransform: 'uppercase', color: '#7c2d12', fontWeight: 700, display: 'block' }}>QA Accepted At</span>
                                <strong style={{ color: '#166534', fontSize: '13px' }}>
                                    {auditData?.acceptedTime ? moment(auditData.acceptedTime).format("DD-MM-YYYY hh:mm A") : "N/A"}
                                </strong>
                            </div>
                            <div style={{ backgroundColor: '#ffffff', border: '1px solid #fed7aa', padding: '10px 14px', borderRadius: '6px' }}>
                                <span style={{ fontSize: '10px', textTransform: 'uppercase', color: '#7c2d12', fontWeight: 700, display: 'block' }}>Delay Duration</span>
                                <strong style={{ color: '#c2410c', fontSize: '13px' }}>
                                    {auditData?.delayMinutes ? `${auditData.delayMinutes} Minutes` : 'Delayed'}
                                </strong>
                            </div>
                            <div style={{ backgroundColor: '#ffffff', border: '1px solid #fed7aa', padding: '10px 14px', borderRadius: '6px' }}>
                                <span style={{ fontSize: '10px', textTransform: 'uppercase', color: '#7c2d12', fontWeight: 700, display: 'block' }}>Accepted By (QA)</span>
                                <strong style={{ color: '#1e293b', fontSize: '13px' }}>
                                    {auditData?.acceptedByName || auditData?.acceptedByEmail || sessionData.cr3ea_tourby || "QA Executive"}
                                </strong>
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. Area Incharges Involved */}
                <div style={{ marginBottom: '25px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', marginBottom: '8px' }}>
                        Area Incharges Involved
                    </div>
                    {areaInchargeCards.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                            {areaInchargeCards.map((card, idx) => (
                                <div key={idx} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981', padding: '10px 12px', borderRadius: '6px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>{card.area}</span>
                                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e293b', marginTop: '3px' }}>{card.names}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px', borderRadius: '6px', color: '#64748b', fontSize: '13px', textAlign: 'center' }}>
                            No specific area incharges configured.
                        </div>
                    )}
                </div>

                {/* 5. Score Card & Daily Tours Count */}
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '25px' }}>
                    <div style={{ flex: 1, minWidth: '280px', border: `1px solid ${isPassingScore ? '#bbf7d0' : '#fecaca'}`, backgroundColor: isPassingScore ? '#f0fdf4' : '#fef2f2', padding: '20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <div style={{ width: '80px', height: '80px', flexShrink: 0, borderRadius: '50%', backgroundColor: scoreColor, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: (isExpired && hasNoScore) ? '14px' : '18px', fontWeight: 'bold', boxShadow: `0 4px 6px -1px ${scoreShadow}` }}>
                            {(isExpired && hasNoScore) ? 'Expired' : `${stats.overallPercent}%`}
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontWeight: 'bold', color: isPassingScore ? '#065f46' : '#991b1b', fontSize: '16px' }}>
                                Overall Compliance Score
                            </h4>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>{scoreDescNode}</p>
                        </div>
                    </div>
                    
                    <div style={{ flex: 1, minWidth: '250px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <div style={{ width: '80px', height: '80px', flexShrink: 0, borderRadius: '50%', backgroundColor: '#64748b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 'bold' }}>
                            <span>{dailyCount}</span>
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontWeight: 'bold', color: '#334155', fontSize: '16px' }}>Conducted ALC Tours Today</h4>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Total sessions logged on this line today.</p>
                        </div>
                    </div>
                </div>

                {/* 6. Recurring Deviation Analysis (Last 5 tours on this line) */}
                {repeatabilityData.length > 0 && (
                    <div className="alert alert-warning" style={{ backgroundColor: '#fffbeb', border: '1px solid #fef3c7', color: '#b45309', padding: '15px 20px', borderRadius: '8px', marginBottom: '25px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            ⚠️ Recurring Deviation Analysis (Last {repeatabilityData[0].totalTours} Tours)
                        </h4>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: 1.6 }}>
                            {repeatabilityData.map((dev, idx) => (
                                <li key={idx}>
                                    <strong>{dev.area}</strong> ({dev.criteria}) — Failed <strong>{dev.times}</strong> times in the last {dev.totalTours} conducted tours on this line.
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* 7. Area-wise Performance Summary Table */}
                <div style={{ marginBottom: '25px' }}>
                    <h4 style={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '12px', fontSize: '15px' }}>Area-wise Performance Summary</h4>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="bs-table" border={1} style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Area Name</th>
                                    <th style={{ padding: '10px' }}>Total Checkpoints</th>
                                    <th style={{ padding: '10px' }}>Passed (Compliant)</th>
                                    <th style={{ padding: '10px' }}>Score</th>
                                    <th style={{ padding: '10px' }}>Compliance Score %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(stats.areaStats).sort().map(areaName => {
                                    const areaStat = stats.areaStats[areaName];
                                    const areaPercentRaw = areaStat.total > 0 ? (areaStat.obtained / areaStat.total) * 100 : 0;
                                    const areaPercent = areaPercentRaw.toFixed(2);
                                    return (
                                        <tr key={areaName}>
                                            <td style={{ padding: '10px', fontWeight: 'bold', textAlign: 'left' }}>{areaName}</td>
                                            <td style={{ padding: '10px' }}>{areaStat.total / 2}</td>
                                            <td style={{ padding: '10px' }}>{areaStat.obtained / 2}</td>
                                            <td style={{ padding: '10px', fontWeight: 'bold' }}>{areaStat.obtained} / {areaStat.total}</td>
                                            <td style={{ padding: '10px' }}>
                                                <span 
                                                    style={{ 
                                                        padding: '3px 8px', 
                                                        borderRadius: '4px', 
                                                        fontSize: '12px', 
                                                        fontWeight: 600,
                                                        backgroundColor: parseFloat(areaPercent) >= 100 ? '#dcfce7' : '#fef3c7',
                                                        color: parseFloat(areaPercent) >= 100 ? '#15803d' : '#b45309'
                                                    }}
                                                >
                                                    {areaPercent}%
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 8. Detailed Checkpoint Log Grouped by Area Blocks */}
                <div>
                    <h4 style={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '15px', fontSize: '15px' }}>
                        📋 Detailed Checkpoint Log
                    </h4>

                    {Object.keys(checkpointsByArea).sort().map(areaName => {
                        const cps = checkpointsByArea[areaName];
                        const inchargeCard = areaInchargeCards.find(c => c.area === areaName);
                        const inchargeNames = inchargeCard?.names || "N/A";

                        return (
                            <div key={areaName} style={{ marginBottom: '25px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                                <div style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                    <h5 style={{ margin: 0, fontWeight: 'bold', color: '#1e293b', fontSize: '14px' }}>{areaName}</h5>
                                    <div style={{ fontSize: '11px', color: '#047857', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', padding: '3px 8px', borderRadius: '4px', fontWeight: 600 }}>
                                        Incharge: {inchargeNames}
                                    </div>
                                </div>

                                <div style={{ overflowX: 'auto', width: '100%' }}>
                                    <table className="bs-table" border={1} style={{ width: '100%', minWidth: '850px', tableLayout: 'fixed', borderCollapse: 'collapse', textAlign: 'center', fontSize: '13px', border: 'none' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#fcfdfe', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>
                                                <th style={{ width: '6%', padding: '8px 10px' }}>Sr No.</th>
                                                <th style={{ width: '40%', padding: '8px 10px', textAlign: 'left' }}>Checkpoint Details</th>
                                                <th style={{ width: '16%', padding: '8px 10px' }}>QA Initial Score</th>
                                                <th style={{ width: '20%', padding: '8px 10px', textAlign: 'left' }}>Corrective Actions Taken</th>
                                                <th style={{ width: '18%', padding: '8px 10px', textAlign: 'left' }}>QA Remarks</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cps.map((cp, idx) => {
                                                const initialScoreText = cp.cr3ea_defectcategory || "Okay (2)";
                                                const isFailed = initialScoreText.includes("(0)") ||
                                                    initialScoreText.includes("(1)") ||
                                                    initialScoreText.includes("Non-Compliant") ||
                                                    initialScoreText.includes("Partial") ||
                                                    initialScoreText === "00" ||
                                                    initialScoreText === "01";

                                                const initialBadge = !isFailed ? (
                                                    <span style={{ backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                                        {initialScoreText}
                                                    </span>
                                                ) : (
                                                    <span style={{ backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                                        {initialScoreText}
                                                    </span>
                                                );

                                                // Corrective Actions Column
                                                let actionsTakenHtml: React.ReactNode = <span className="text-muted">-</span>;
                                                let prodRemark = cp.cr3ea_productionremarks || "";

                                                if (!prodRemark) {
                                                    const defectRemarks = cp.cr3ea_defectremarks || "";
                                                    if (defectRemarks.startsWith("Action:")) {
                                                        prodRemark = defectRemarks;
                                                    }
                                                }

                                                if (prodRemark) {
                                                    if (prodRemark.includes(" | Re-verified:")) {
                                                        prodRemark = prodRemark.split(" | Re-verified:")[0].trim();
                                                    }
                                                    let textPart = prodRemark;
                                                    if (prodRemark.startsWith("Action: ")) {
                                                        textPart = prodRemark.replace("Action: ", "");
                                                    }
                                                    let fileName = "";
                                                    if (textPart.includes("| File:")) {
                                                        const parts = textPart.split("| File:");
                                                        textPart = parts[0].trim();
                                                        fileName = parts[1] ? parts[1].trim() : "";
                                                    }

                                                    if (fileName) {
                                                        let prodFiles = fileName.split(",").map((f: string) => f.trim()).filter(Boolean);
                                                        actionsTakenHtml = (
                                                            <div>
                                                                <div>{textPart}</div>
                                                                <div style={{ marginTop: '4px' }}>
                                                                    {prodFiles.map((f: string, fIdx: number) => (
                                                                        <a 
                                                                            key={fIdx}
                                                                            href={`https://bectorsfood.sharepoint.com/sites/ptms/ALC_CorrectiveActions_Docs/${f}`} 
                                                                            target="_blank" 
                                                                            rel="noreferrer" 
                                                                            className="no-print" 
                                                                            style={{ textDecoration: 'underline', color: '#1a73e8', fontWeight: 'bold', fontSize: '11px', marginRight: '6px' }}
                                                                        >
                                                                            View Proof{prodFiles.length > 1 ? ` (${fIdx + 1})` : ''}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    } else {
                                                        actionsTakenHtml = textPart;
                                                    }
                                                }

                                                // QA Remarks Column
                                                const qaRemark = cp.cr3ea_defectremarks || "";
                                                let qaRemarksHtml: React.ReactNode = <span className="text-muted">-</span>;

                                                const parts = qaRemark.split(" | Re-verified:");
                                                let initialPart = parts[0] ? parts[0].trim() : "";
                                                const reverifyParts = parts.slice(1).map((p: string) => p.trim());

                                                let qaFileName = "";
                                                if (initialPart.toLowerCase().includes("file:")) {
                                                    const fIdx = initialPart.toLowerCase().indexOf("file:");
                                                    qaFileName = initialPart.substring(fIdx + 5).trim();
                                                    let textPart = initialPart.substring(0, fIdx).trim();
                                                    if (textPart.endsWith("|")) {
                                                        textPart = textPart.substring(0, textPart.length - 1).trim();
                                                    }
                                                    initialPart = textPart;
                                                }

                                                if (!initialPart && qaFileName) {
                                                    initialPart = "Image Proof Uploaded";
                                                }

                                                const qaFiles = qaFileName ? qaFileName.split(",").map((f: string) => f.trim()).filter(Boolean) : [];

                                                const showInitial = initialPart && !initialPart.startsWith("Action:");

                                                qaRemarksHtml = (
                                                    <div>
                                                        {showInitial && (
                                                            <div>
                                                                <span>{initialPart}</span>
                                                                {qaFiles.map((f: string, i: number) => (
                                                                    <a 
                                                                        key={i}
                                                                        href={`https://bectorsfood.sharepoint.com/sites/ptms/ALC_CorrectiveActions_Docs/${f}`} 
                                                                        target="_blank" 
                                                                        rel="noreferrer" 
                                                                        className="no-print" 
                                                                        style={{ textDecoration: 'underline', color: '#1a73e8', fontWeight: 'bold', fontSize: '11px', marginLeft: '5px' }}
                                                                    >
                                                                        View QA Proof{qaFiles.length > 1 ? ` (${i + 1})` : ''}
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {reverifyParts.map((rev: string, revIdx: number) => {
                                                            let cleanRev = rev;
                                                            let revFileName = "";
                                                            if (rev.toLowerCase().includes("file:")) {
                                                                const idx2 = rev.toLowerCase().indexOf("file:");
                                                                revFileName = rev.substring(idx2 + 5).trim();
                                                                let text2 = rev.substring(0, idx2).trim();
                                                                if (text2.endsWith("|")) text2 = text2.substring(0, text2.length - 1).trim();
                                                                cleanRev = text2;
                                                            }
                                                            if (!cleanRev && revFileName) cleanRev = "Image Proof Uploaded";
                                                            const revFiles = revFileName ? revFileName.split(",").map((f: string) => f.trim()).filter(Boolean) : [];

                                                            return (
                                                                <div key={revIdx} style={{ color: '#15803d', fontWeight: 'bold', fontSize: '11px', marginTop: '4px' }}>
                                                                    Re-verified: {cleanRev}
                                                                    {revFiles.map((f: string, fi: number) => (
                                                                        <a 
                                                                            key={fi}
                                                                            href={`https://bectorsfood.sharepoint.com/sites/ptms/ALC_CorrectiveActions_Docs/${f}`} 
                                                                            target="_blank" 
                                                                            rel="noreferrer" 
                                                                            className="no-print" 
                                                                            style={{ textDecoration: 'underline', color: '#1a73e8', fontWeight: 'bold', fontSize: '11px', marginLeft: '5px' }}
                                                                        >
                                                                            View Re-verify Proof{revFiles.length > 1 ? ` (${fi + 1})` : ''}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );

                                                return (
                                                    <tr key={cp.cr3ea_rajpura_alcsid || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={{ padding: '10px' }}>{idx + 1}</td>
                                                        <td style={{ padding: '10px', textAlign: 'left', wordBreak: 'break-word' }}>
                                                            {cp._isCritical && (
                                                                <span style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', marginRight: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-block' }}>
                                                                    Critical Gate
                                                                </span>
                                                            )}
                                                            {cp.cr3ea_criteria}
                                                        </td>
                                                        <td style={{ padding: '10px' }}>{initialBadge}</td>
                                                        <td style={{ padding: '10px', textAlign: 'left', fontSize: '12px', wordBreak: 'break-word' }}>
                                                            {actionsTakenHtml}
                                                        </td>
                                                        <td style={{ padding: '10px', textAlign: 'left', fontSize: '12px', wordBreak: 'break-word' }}>
                                                            {qaRemarksHtml}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>

            </div>
        </div>
    );
};

export default ALCSummary;
